import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { type DirectSubItemForm, getSubItemLabel } from './subItemTypes';
import { useSubItemTypeMeta } from '@/contexts/SubItemCategoriesContext';
import { SubItemRowWrapper } from './SubItemRowWrapper';
import { MarkupRateBadge } from './MarkupRateBadge';

interface DirectSubItemRowProps {
  item: DirectSubItemForm;
  isNegative?: boolean;
  isEditingNote: boolean;
  onUpdate: (updates: Partial<DirectSubItemForm>) => void;
  onDelete: () => void;
  onStartEditingNote: () => void;
  onStopEditingNote: () => void;
  readOnly?: boolean;
  jobMarkupRate: number;
  isSelected?: boolean;
  onClick?: () => void;
}

export function DirectSubItemRow({
  item,
  isNegative = false,
  isEditingNote,
  onUpdate,
  onDelete,
  onStartEditingNote,
  onStopEditingNote,
  readOnly = false,
  jobMarkupRate,
  isSelected = false,
  onClick,
}: DirectSubItemRowProps) {
  const typeMeta = useSubItemTypeMeta();
  const [localValue, setLocalValue] = useState(
    item.amount ? item.amount.toFixed(2) : ''
  );
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(item.amount ? item.amount.toFixed(2) : '');
    }
  }, [item.amount, isFocused]);

  return (
    <SubItemRowWrapper
      label={getSubItemLabel(item.type, typeMeta)}
      note={item.note}
      isEditingNote={isEditingNote}
      onNoteChange={(note) => onUpdate({ note })}
      onStartEditingNote={onStartEditingNote}
      onStopEditingNote={onStopEditingNote}
      onDelete={onDelete}
      readOnly={readOnly}
      isSelected={isSelected}
      onClick={onClick}
    >
      <div className="flex flex-col items-end gap-0.5">
        <div className="relative w-24 ml-1">
          <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground">
            {isNegative && item.amount > 0 ? '-$' : '$'}
          </span>

          <Input
            type="number"
            min="0"
            step="0.01"
            value={localValue}
            onChange={(e) => {
              const nextValue = e.target.value;
              setLocalValue(nextValue);
              const val = parseFloat(nextValue);
              if (!isNaN(val)) {
                onUpdate({ amount: Math.round(val * 100) / 100 });
              } else {
                onUpdate({ amount: 0 });
              }
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={(e) => {
              setIsFocused(false);
              const val = parseFloat(e.target.value);
              if (!isNaN(val)) {
                setLocalValue(val.toFixed(2));
              } else {
                setLocalValue('');
              }
            }}
            placeholder="0.00"
            className="
              h-7 w-24 pl-5 pr-2 text-right tabular-nums
              bg-transparent
              [appearance:textfield]
              [&::-webkit-outer-spin-button]:appearance-none
              [&::-webkit-inner-spin-button]:appearance-none
            "
            disabled={readOnly}
          />
        </div>
        {item.type === 'materials' && (
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-gray-400">Markup:</span>
            <MarkupRateBadge
              markupRate={item.markupRate}
              jobMarkupRate={jobMarkupRate}
              onChange={(markupRate) => onUpdate({ markupRate })}
              readOnly={readOnly}
            />
          </div>
        )}
      </div>
    </SubItemRowWrapper>
  );
}
